import React, { useEffect } from 'react';
import { X, CheckCircle2, Sparkles, FlaskConical, MessageCircle } from 'lucide-react';
import { CATEGORY_LABELS } from '../../data/productsData';

const WA_NUMBER = '919410110910';

export const ProductModal = ({ product, onClose }) => {
  // Scroll lock + ESC close
  useEffect(() => {
    if (!product) return undefined;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKey);
    };
  }, [product, onClose]);

  if (!product) return null;

  const waUrl = `https://api.whatsapp.com/send?phone=${WA_NUMBER}&text=${encodeURIComponent(
    `Hi EntoRid! I'm interested in your upcoming product "${product.name}" (${CATEGORY_LABELS[product.category]}). Please notify me when it launches.`
  )}`;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6 modal-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="product-modal-backdrop"
    >
      <div
        className="modal-panel relative w-full sm:max-w-2xl max-h-[92vh] sm:max-h-[88vh] bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-label={product.name}
        data-testid="product-modal"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-black/40 backdrop-blur text-white flex items-center justify-center hover:bg-black/60 transition-colors"
          aria-label="Close"
          data-testid="product-modal-close"
        >
          <X size={18} />
        </button>

        <div className="overflow-y-auto">
          {/* Image */}
          <div className="relative h-52 sm:h-64 bg-slate-100">
            <img src={product.image} alt={product.name} className="w-full h-full object-cover saturate-[0.85]" />
            <div className="absolute inset-0 bg-slate-900/35" aria-hidden />
            <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
            {/* COMING SOON ribbon */}
            <div className="absolute top-4 right-4 z-10">
              <div className="bg-gradient-to-r from-amber-400 to-amber-500 text-amber-950 text-xs font-extrabold uppercase tracking-wider px-3 py-1.5 rounded-full shadow-lg ring-2 ring-white/40 flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-amber-900 animate-pulse" />
                Coming Soon
              </div>
            </div>
            <div className="absolute bottom-4 left-5 right-5">
              <span className="inline-flex px-2.5 py-1 rounded-full bg-emerald-500 text-white text-[11px] font-bold uppercase tracking-wide">
                {CATEGORY_LABELS[product.category]}
              </span>
              <h2 className="mt-2 text-xl sm:text-2xl font-bold text-white leading-tight" data-testid="product-modal-title">
                {product.name}
              </h2>
            </div>
          </div>

          {/* Body */}
          <div className="p-5 sm:p-7 space-y-6">
            <p className="text-sm sm:text-base text-slate-600 leading-relaxed">{product.fullDescription}</p>

            <div>
              <h3 className="flex items-center gap-2 text-sm font-bold text-slate-900 uppercase tracking-wide">
                <CheckCircle2 size={16} className="text-emerald-600" /> Key Features
              </h3>
              <ul className="mt-3 grid sm:grid-cols-2 gap-2">
                {product.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-slate-600">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="flex items-center gap-2 text-sm font-bold text-slate-900 uppercase tracking-wide">
                <Sparkles size={16} className="text-amber-500" /> Benefits
              </h3>
              <ul className="mt-3 grid sm:grid-cols-2 gap-2">
                {product.benefits.map((b) => (
                  <li key={b} className="flex items-start gap-2 text-sm text-slate-600">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" />
                    {b}
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-slate-50 border border-slate-100 rounded-2xl p-4">
              <h3 className="flex items-center gap-2 text-sm font-bold text-slate-900 uppercase tracking-wide">
                <FlaskConical size={16} className="text-sky-600" /> Recommended Usage
              </h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{product.usage}</p>
            </div>
          </div>
        </div>

        {/* Sticky CTA */}
        <div className="border-t border-slate-100 bg-white p-4 sm:px-7 flex-shrink-0">
          <div className="mb-3 p-3 rounded-xl bg-amber-50 border border-amber-200 text-center">
            <p className="text-xs font-bold text-amber-900 uppercase tracking-wide">🚀 Product Coming Soon</p>
            <p className="text-[11px] text-amber-800/80 mt-0.5">Currently we offer pest control services only. This product launches soon!</p>
          </div>
          <a
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full h-12 rounded-full bg-[#25D366] text-white font-bold text-sm sm:text-base hover:bg-[#1fb958] active:scale-[0.98] transition-[background-color,transform] duration-200"
            data-testid="product-modal-whatsapp-cta"
          >
            <MessageCircle size={18} />
            Notify Me on WhatsApp
          </a>
        </div>
      </div>
    </div>
  );
};
